/**
 * Scoring Algorithm for AI Visibility
 * Sources: see src/data/research-sources.ts for full metadata
 */
/**
 * Scoring Algorithm for AI Visibility
 *
 * Point allocation:
 * - ChatGPT: 0 to 40 points
 * - Google Search: 0 to 30 points
 *
 * Maximum score: 70 (2 platforms scanned).
 *
 * The original design reserved a further 30 points for a third signal
 * covering other AI platforms. It was designed but never implemented;
 * the project closed first. No version of AILens ever measured it.
 */

export interface ScoringInput {
  aiPlatformsFound: boolean;
  aiScore?: number;              // V2.1: Direct score from Discovery Mode (0, 20, 40)
  googleFound: boolean;
  googlePosition: number | null;
  googleConfidence?: 'high' | 'medium' | 'low'; // V2.1: Kept for backwards compatibility but not used
  isPaidUser?: boolean;
  // V2.1 Discovery Mode fields
  queryType?: 'discovery' | 'fallback' | 'not_found' | 'error';
  queryUsed?: string;
  rawResponseSnippet?: string;
}

export interface ScoringResult {
  currentScore: number;
  potentialScore: number;
  visibilityGap: number;
  platformsCovered: number;
  totalPlatforms: number;
  coveragePercent: number;
  statusMessage: string;
  scoreColor: 'emerald' | 'amber' | 'rose';
  aiPlatformsScore: number;
  googleAdjustedScore: number;
}

/**
 * V2.1 REWARD MODEL: Calculate Google score based on ranking position
 * No penalties - only rewards for good positions
 * 
 * Research: retrieval position drives citation rank (Puerto et al., NeurIPS 2025).
 * First position gained +2.77 rank, position 10 lost 0.78 (retail domain, gpt-4o-mini).
 * See research-sources.ts (neurips-2025).
 */
function calculateGoogleScore(position: number | null): number {
  // Not found or invalid position
  if (position === null || position === undefined || position <= 0) {
    return 0;
  }
  
  // Position 1-3: Excellent - AI citation zone
  // Research shows top 3 get majority of AI citations
  if (position <= 3) {
    return 30;
  }
  
  // Position 4-10: Good - still on page 1
  // Solid SEO foundation
  if (position <= 10) {
    return 20;
  }
  
  // Position 11-20: Present - on the radar
  // Has some visibility but needs improvement
  if (position <= 20) {
    return 10;
  }
  
  // Position 21+: Not effectively visible
  return 0;
}

/**
 * Get score color based on value (0 to 70 scale)
 * Thresholds match the published score-range table on the methodology page.
 */
function getScoreColor(score: number, _isPaid: boolean): ScoringResult['scoreColor'] {
  if (score >= 50) return 'emerald';  // Strong foundation
  if (score >= 30) return 'amber';    // Partial visibility
  return 'rose';                       // Low visibility
}

/**
 * Get status message based on score (0 to 70 scale)
 * Thresholds match the published score-range table on the methodology page.
 */
function getStatusMessage(score: number, _isPaid: boolean): string {
  if (score >= 50) return 'Strong foundation. Visible on scanned platforms';
  if (score >= 30) return 'Partial visibility. Room for improvement';
  return 'Low visibility. Significant optimization needed';
}

/**
 * Calculate the visibility score
 */
export function calculateVisibilityScore(input: ScoringInput): ScoringResult {
  const { aiScore, aiPlatformsFound, googleFound, googlePosition, isPaidUser = false } = input;
  
  // V2.1: AI Score comes directly from Discovery Mode (0, 20, or 40)
  // Use the explicit aiScore if provided, otherwise fall back to boolean conversion
  const aiPlatformsScore = aiScore !== undefined ? aiScore : (aiPlatformsFound ? 40 : 0);
  
  // V2.1: Google score uses Reward Model (no penalties)
  const googleAdjustedScore = googleFound ? calculateGoogleScore(googlePosition) : 0;
  
  // Placeholders for the third signal that was designed but never implemented.
  // Always 0; retained only so the sum below stays explicit about what is missing.
  const perplexityScore = 0;
  const claudeScore = 0;
  const geminiScore = 0;
  
  // V2.1: Simplified scoring - direct addition of component scores
  // AI score (0-40) + Google score (0-30) = 0-70 free tier max
  const rawScore = aiPlatformsScore + googleAdjustedScore + perplexityScore + claudeScore + geminiScore;
  
  const currentScore = Math.round(rawScore);
  
  // Calculate potential score (realistic estimate based on current data)
  // If found on AI platforms and Google, potential is higher
  const potentialMultiplier = aiPlatformsFound && googleFound ? 1.0 : 
                              aiPlatformsFound || googleFound ? 0.9 : 0.8;
  const potentialScore = Math.round(70 * potentialMultiplier);

  const visibilityGap = potentialScore - currentScore;

  // Platform coverage. Both scanned platforms are the only ones that ever existed.
  const platformsCovered = 2;
  const totalPlatforms = 2;
  const coveragePercent = Math.round((platformsCovered / totalPlatforms) * 100);
  
  return {
    currentScore,
    potentialScore,
    visibilityGap,
    platformsCovered,
    totalPlatforms,
    coveragePercent,
    statusMessage: getStatusMessage(currentScore, isPaidUser),
    scoreColor: getScoreColor(currentScore, isPaidUser),
    aiPlatformsScore,
    googleAdjustedScore,
  };
}
